import React from 'react';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material';
import { t } from 'i18next';

interface ConfirmDialogProps {
  showDialogFlag: boolean;
  handleClose: (value: React.SetStateAction<boolean>) => void;
  dialogTitle: string;
  dialogContentText: string;
  showDialog: (value: React.SetStateAction<boolean>) => void;
  confirmDialog: () => void | Promise<void>;
  variant?: 'default' | 'warning';
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  showDialogFlag,
  handleClose,
  dialogTitle,
  dialogContentText,
  showDialog,
  confirmDialog,
  variant = 'default',
}) => {
  const isWarning = variant === 'warning';
  const theme = useTheme();

  return (
    <Dialog
      open={showDialogFlag}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: isWarning ? 480 : 420,
          borderRadius: '12px',
          boxShadow: isWarning ? theme.shadows[6] : undefined,
          border: isWarning ? `1px solid ${theme.palette.divider}` : undefined,
        },
      }}>
      <DialogTitle id="alert-dialog-title" sx={{ pb: isWarning ? 1 : 2, pt: 2.5, px: 3 }}>
        {isWarning ? (
          <Box display="flex" alignItems="center" gap={1.25}>
            <WarningAmberRoundedIcon sx={{ color: theme.palette.warning.main, fontSize: 22 }} />
            <Typography
              fontWeight={700}
              fontSize={22}
              lineHeight={1.2}
              color={theme.palette.text.primary}>
              {t(dialogTitle)}
            </Typography>
          </Box>
        ) : (
          t(dialogTitle)
        )}
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: isWarning ? 1 : 2 }}>
        <DialogContentText
          id="alert-dialog-description"
          sx={{
            color: isWarning ? theme.palette.text.secondary : undefined,
            fontSize: isWarning ? 15 : undefined,
            lineHeight: isWarning ? 1.55 : undefined,
            mt: isWarning ? 0.5 : 0,
          }}>
          {dialogContentText}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: isWarning ? 1.5 : 1, gap: 1 }}>
        <Button
          onClick={() => showDialog(false)}
          sx={{
            minWidth: 88,
            height: 40,
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            color: isWarning ? theme.palette.text.secondary : undefined,
          }}>
          {t('adminMainPage.operation.cancel')}
        </Button>
        <Button
          onClick={confirmDialog}
          variant="contained"
          sx={{
            minWidth: 88,
            height: 40,
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            '&:hover': {
              boxShadow: 'none',
            },
          }}>
          {t('adminMainPage.operation.ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
